import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, Globe, ExternalLink, Mail, Phone } from "lucide-react";
import { EditableText } from "@/components/admin/EditableText";
import { EditableImage } from "@/components/admin/EditableImage";

interface CompanyInfoSectionProps {
  product: any;
  isEditor: boolean;
  isEditMode: boolean;
  onUpdateField: (field: string, value: any) => Promise<void>;
  onBookCall?: () => void;
}

export const CompanyInfoSection = ({
  product,
  isEditor,
  isEditMode,
  onUpdateField,
  onBookCall,
}: CompanyInfoSectionProps) => {
  const companyInfo = product.company_info || {};
  const hasCompanyInfo = companyInfo && Object.keys(companyInfo).length > 0;
  
  // Extract founder/vendor info
  const vendorName = product.vendor_name || product.title;
  const vendorCompany = product.vendor_company;
  const profileImage = product.profile_image || product.image;

  const handleUpdateCompanyInfo = async (field: string, value: string) => {
    const updatedCompanyInfo = {
      ...companyInfo,
      [field]: value,
    };
    await onUpdateField('company_info', updatedCompanyInfo);
  };

  const handleUpdateInstructorInfo = async (field: string, value: any) => {
    const updatedInstructorInfo = {
      ...(product.instructor_info || {}),
      [field]: value,
    };
    await onUpdateField('instructor_info', updatedInstructorInfo);
  };
  
  return (
    <div className="grid lg:grid-cols-[280px,1fr] gap-8">
      {/* Left Column - Compact Company Info Card */}
      <div className="lg:sticky lg:top-40 h-fit">
        <Card className="p-5">
          {/* Avatar and Name */}
          <div className="flex flex-col items-center text-center mb-4">
            {isEditor && isEditMode ? (
              <EditableImage
                src={profileImage}
                alt={vendorName}
                onSave={(url) => onUpdateField('profile_image', url)}
                className="h-20 w-20 rounded-full object-cover"
                containerClassName="mb-3"
              />
            ) : (
              <Avatar className="h-20 w-20 mb-3 ring-2 ring-primary/20">
                <AvatarImage src={profileImage} alt={vendorName} />
                <AvatarFallback className="text-xl">{vendorName?.[0]}</AvatarFallback>
              </Avatar>
            )}
            <h3 className="font-bold text-lg">
              {isEditor && isEditMode ? (
                <EditableText
                  value={vendorName || ''}
                  onSave={(value) => onUpdateField('vendor_name', value)}
                />
              ) : (
                vendorName
              )}
            </h3>
            {(vendorCompany || (isEditor && isEditMode)) && (
              <p className="text-sm text-muted-foreground">
                {isEditor && isEditMode ? (
                  <EditableText
                    value={vendorCompany || ''}
                    onSave={(value) => onUpdateField('vendor_company', value)}
                  />
                ) : (
                  vendorCompany
                )}
              </p>
            )}
          </div>
          
          {/* Quick Stats */}
          <div className="space-y-3 border-t pt-4">
            {/* Vendor Email - Admin only */}
            {isEditor && isEditMode && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <EditableText
                  value={product.vendor_email || ''}
                  onSave={(value) => onUpdateField('vendor_email', value)}
                  className="flex-1"
                />
              </div>
            )}
            
            {/* Vendor Phone - Admin only */}
            {isEditor && isEditMode && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <EditableText
                  value={product.vendor_phone || ''}
                  onSave={(value) => onUpdateField('vendor_phone', value)}
                  className="flex-1"
                />
              </div>
            )}
            
            {(companyInfo?.team_size || (isEditor && isEditMode)) && (
              <div className="flex items-center gap-3 text-sm">
                <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                {isEditor && isEditMode ? (
                  <EditableText
                    value={companyInfo?.team_size || ''}
                    onSave={(value) => handleUpdateCompanyInfo('team_size', value)}
                    className="flex-1"
                  />
                ) : (
                  <span>{companyInfo.team_size}</span>
                )}
              </div>
            )}
            {(companyInfo?.website || (isEditor && isEditMode)) && (
              <div className="flex items-center gap-3 text-sm">
                <Globe className="h-4 w-4 text-blue-500 flex-shrink-0" />
                {isEditor && isEditMode ? (
                  <EditableText
                    value={companyInfo?.website || ''}
                    onSave={(value) => handleUpdateCompanyInfo('website', value)}
                    className="flex-1"
                  />
                ) : (
                  <a 
                    href={companyInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline flex items-center gap-1 truncate"
                  >
                    {companyInfo.website.replace(/^https?:\/\//, '').split('/')[0]}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Book Call Button */}
          {onBookCall && product.booking_url && (
            <Button onClick={onBookCall} className="w-full mt-4">
              Book a Call
            </Button>
          )}
        </Card>
      </div>
      
      {/* Right Column - Full Description + Founder Profile */}
      <div className="space-y-6">
        {/* Full Description */}
        <div>
          <h2 className="text-2xl font-bold mb-4">About {product.title}</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground">
            {isEditor && isEditMode ? (
              <EditableText
                value={product.description || ''}
                onSave={(value) => onUpdateField('description', value)}
                multiline
                className="min-h-[100px]"
              />
            ) : (
              <p className="whitespace-pre-wrap">{product.description}</p>
            )}
          </div>
        </div>
        
        {/* Founder Profile Section */}
        {(product.instructor_info || vendorName || (isEditor && isEditMode)) && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Meet the Founder</h3>
            <div className="flex items-start gap-4">
              {isEditor && isEditMode ? (
                <EditableImage
                  src={product.instructor_info?.avatar || profileImage}
                  alt={vendorName}
                  onSave={(url) => handleUpdateInstructorInfo('avatar', url)}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <Avatar className="h-16 w-16 ring-2 ring-primary/10">
                  <AvatarImage src={product.instructor_info?.avatar || profileImage} alt={vendorName} />
                  <AvatarFallback>{vendorName?.[0]}</AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1">
                <h4 className="font-bold">
                  {isEditor && isEditMode ? (
                    <EditableText
                      value={product.instructor_info?.name || vendorName || ''}
                      onSave={(value) => handleUpdateInstructorInfo('name', value)}
                    />
                  ) : (
                    product.instructor_info?.name || vendorName
                  )}
                </h4>
                {(product.instructor_info?.title || (isEditor && isEditMode)) && (
                  <p className="text-sm text-muted-foreground">
                    {isEditor && isEditMode ? (
                      <EditableText
                        value={product.instructor_info?.title || ''}
                        onSave={(value) => handleUpdateInstructorInfo('title', value)}
                      />
                    ) : (
                      product.instructor_info.title
                    )}
                  </p>
                )}
                {(product.instructor_info?.bio || (isEditor && isEditMode)) && (
                  <div className="text-sm text-muted-foreground mt-2">
                    {isEditor && isEditMode ? (
                      <EditableText
                        value={product.instructor_info?.bio || ''}
                        onSave={(value) => handleUpdateInstructorInfo('bio', value)}
                        multiline
                      />
                    ) : (
                      <p>{product.instructor_info.bio}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};